import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, Settings2, ShieldCheck, Users } from "lucide-react";

import { api } from "../lib/api";
import {
  AUTHORITY_LABEL, READINESS_LABEL, READINESS_TONE, centavosFromPesosInput,
  detailMessage, humanize, pesosFromCentavos,
} from "../lib/driverService";

const TABS = [
  { key: "overview", label: "Overview", icon: ShieldCheck },
  { key: "drivers", label: "Drivers", icon: Users },
  { key: "queue", label: "Needs a driver", icon: AlertTriangle },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "settings", label: "Settings", icon: Settings2 },
];

export function Badge({ status, children, testId }) {
  return (
    <span data-testid={testId}
          className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${READINESS_TONE[status] || READINESS_TONE.UNAVAILABLE}`}>
      {children || READINESS_LABEL[status] || humanize(status)}
    </span>
  );
}

export default function DriverService() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "overview";
  const [overview, setOverview] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    api.get("/driver-service/overview").then((r) => setOverview(r.data)).catch((err) => {
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(detailMessage(err));
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600" data-testid="ds-denied">
      You don&apos;t have access to driver service. Ask the business owner for a driver permission.</div>;
  }
  const caps = overview?.capabilities || [];

  return (
    <div className="space-y-5" data-testid="driver-service-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Driver service</h1>
          <p className="text-slate-600 mt-1 text-sm">Who can drive which trip, and whether each trip is ready to go.
            Every driver is checked again when assigned and before the trip starts.</p>
        </div>
        <Link to="/app/drivers" className="text-sm underline text-slate-700">Driver records</Link>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} data-testid={`ds-tab-${t.key}`}
                  onClick={() => setParams({ tab: t.key })}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${
                    tab === t.key ? "border-emerald-600 text-emerald-800 font-medium" : "border-transparent text-slate-600"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview data={overview} />}
      {tab === "drivers" && <DriverList />}
      {tab === "queue" && <Queue />}
      {tab === "schedule" && <Schedule />}
      {tab === "settings" && <SettingsPanel canEdit={caps.includes("drivers.settings.manage")} onSaved={load} />}
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-900", testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone}`}>{value ?? "-"}</div>
    </div>
  );
}

function Overview({ data }) {
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const c = data.counts;
  return (
    <div className="space-y-5" data-testid="ds-overview">
      {!data.service.enabled && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          Driver service is turned off. Turn it on in Settings to take with-driver bookings.
        </div>
      )}
      {c.bookings_needing_driver > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="ds-needing-banner">
          {c.bookings_needing_driver} booking{c.bookings_needing_driver === 1 ? "" : "s"} need a driver.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Ready now" value={c.ready_now} tone="text-emerald-700" testId="ds-stat-ready" />
        <Stat label="Review required" value={c.review_required} tone="text-amber-700" />
        <Stat label="Blocked" value={c.blocked} tone={c.blocked ? "text-rose-700" : "text-slate-900"} testId="ds-stat-blocked" />
        <Stat label="Unavailable" value={c.unavailable} />
        <Stat label="Licences to verify" value={c.licence_needs_verification} />
        <Stat label="Licences expiring" value={c.licence_expiring} />
        <Stat label="Safety holds" value={c.safety_holds} />
        <Stat label="Trips today" value={c.trips_today} />
      </div>
      <p className="text-xs text-slate-500">{data.note}</p>
      {data.alerts?.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold text-sm">Alerts</h2>
          <ul className="mt-2 divide-y divide-slate-100" data-testid="ds-alerts">
            {data.alerts.map((a) => (
              <li key={a.id} className="py-2 text-sm flex flex-wrap items-center justify-between gap-2">
                <span>{a.message}</span>
                {a.booking_id && <Link className="text-xs underline" to={`/app/bookings/${a.booking_id}`}>Open booking</Link>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function DriverList() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    api.get("/driver-service/drivers", { params: filter ? { readiness: filter } : {} })
      .then((r) => setRows(r.data.items)).catch((err) => toast.error(detailMessage(err)));
  }, [filter]);
  return (
    <div className="space-y-3" data-testid="ds-drivers">
      <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by readiness"
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
        <option value="">All drivers</option>
        {Object.entries(READINESS_LABEL).filter(([k]) => k !== "NOT_APPLICABLE").map(([k, v]) =>
          <option key={k} value={k}>{v}</option>)}
      </select>
      {!rows && <div className="text-slate-500">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">
          No drivers yet. Mark a staff member as a driver in Employees, or add an external driver.
        </div>
      )}
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(rows || []).map((d) => (
          <li key={d.profile_id}>
            <Link to={`/app/driver-service/drivers/${d.profile_id}`} data-testid={`ds-driver-${d.profile_id}`}
                  className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-600">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.display_name}</div>
                  <div className="text-xs text-slate-500">{humanize(d.relationship_type)}{d.has_login ? "" : " · no login"}</div>
                </div>
                <Badge status={d.readiness_now} />
              </div>
              <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                <div>{AUTHORITY_LABEL[d.driving_authority] || humanize(d.driving_authority)}
                  {d.licence_verified_by ? ` · verified by ${d.licence_verified_by.toLowerCase()}` : ""}</div>
                {d.dl_codes?.length > 0 && <div>DL codes: {d.dl_codes.join(", ")}</div>}
                <div>{d.upcoming_trips} upcoming trip{d.upcoming_trips === 1 ? "" : "s"}</div>
                {d.reason_codes?.length > 0 && <div className="text-amber-800">{d.reason_codes.map(humanize).join(" · ")}</div>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Queue() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/driver-service/queue").then((r) => setData(r.data)).catch((err) => toast.error(detailMessage(err)));
  }, []);
  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.items.length === 0) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="ds-queue-empty">
      Every upcoming with-driver booking has a driver.</div>;
  }
  return (
    <ul className="space-y-2" data-testid="ds-queue">
      {data.items.map((b) => (
        <li key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium">{b.booking_reference} · {b.vehicle_name || "Vehicle"}</div>
            <div className="text-xs text-slate-500">{new Date(b.start_datetime).toLocaleString()} · {b.customer_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge status={b.driver_assignment_status === "replacement_required" ? "BLOCKED"
              : b.urgency === "OVERDUE" ? "BLOCKED" : "REVIEW_REQUIRED"}>{b.label}</Badge>
            <Link to={`/app/bookings/${b.id}`} className="text-sm px-3 py-1.5 rounded-lg btn-brand">Assign</Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Schedule() {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [data, setData] = useState(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * 86400000)), [weekStart]);
  useEffect(() => {
    const end = new Date(weekStart.getTime() + 7 * 86400000);
    api.get("/driver-service/scheduler", { params: { start: weekStart.toISOString(), end: end.toISOString() } })
      .then((r) => setData(r.data)).catch((err) => toast.error(detailMessage(err)));
  }, [weekStart]);
  const onDay = (items, day, startKey, endKey) => items.filter((x) => {
    const s = new Date(x[startKey]); const e = new Date(x[endKey]);
    const d0 = day.getTime(); const d1 = d0 + 86400000;
    return s.getTime() < d1 && e.getTime() > d0;
  });
  return (
    <div className="space-y-3" data-testid="ds-schedule">
      <div className="flex items-center gap-2">
        <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))}>Previous week</button>
        <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))}>Next week</button>
      </div>
      {data?.unassigned_with_driver?.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {data.unassigned_with_driver.length} with-driver booking(s) this week have no driver.
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[720px] w-full text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-2 w-40">Driver</th>
              {days.map((d) => <th key={d.toISOString()} className="p-2 text-left">{d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data?.drivers || []).map((lane) => (
              <tr key={lane.profile_id} className="border-t border-slate-100 align-top">
                <td className="p-2 font-medium">{lane.display_name}
                  {lane.safety_status === "SAFETY_HOLD" && <div className="text-rose-700">Safety hold</div>}</td>
                {days.map((d) => (
                  <td key={d.toISOString()} className="p-1">
                    {onDay(lane.trips, d, "start_datetime", "end_datetime").map((t) => (
                      <Link key={t.id} to={`/app/bookings/${t.id}`} className="block rounded bg-emerald-50 border border-emerald-200 px-1 py-0.5 mb-1 truncate">
                        {t.booking_reference}</Link>
                    ))}
                    {onDay(lane.time_off, d, "start_at", "end_at").map((b) => (
                      <div key={b.id} className="rounded bg-slate-100 border border-slate-200 px-1 py-0.5 mb-1">{humanize(b.block_type)}</div>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsPanel({ canEdit, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/driver-service/settings").then((r) => {
    setData(r.data);
    const s = r.data.settings;
    setForm({ ...s, rate_pesos: s.rate_centavos == null ? "" : (s.rate_centavos / 100).toFixed(2) });
  }).catch((err) => toast.error(detailMessage(err))), []);
  useEffect(() => { load(); }, [load]);
  if (!data || !form) return <div className="text-slate-500">Loading…</div>;

  const save = async () => {
    const payload = {};
    ["enabled", "publicly_offered", "assignment_mode", "acceptance_required", "rate_type", "availability_mode",
     "min_lead_time_hours", "assignment_deadline_hours", "contact_release_hours", "turnaround_buffer_minutes",
     "fit_for_duty_required", "require_internal_authorization", "dispatch_review_policy", "show_driver_first_name",
     "service_terms"].forEach((k) => { payload[k] = form[k]; });
    if (form.rate_pesos !== "") {
      const cents = centavosFromPesosInput(form.rate_pesos);
      if (cents === null) { toast.error("Enter the rate in pesos, e.g. 1500 or 1500.50"); return; }
      payload.rate_centavos = cents;
    }
    setSaving(true);
    try {
      await api.put("/driver-service/settings", payload);
      toast.success("Driver service settings saved");
      await load();
      onSaved?.();
    } catch (err) { toast.error(detailMessage(err)); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked
    : e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const offer = data.public_offer;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="ds-settings">
      <div className={`rounded-lg border p-3 text-sm ${offer.offered ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-slate-50 text-slate-700"}`}
           data-testid="ds-public-offer">
        {offer.offered ? "Customers can choose “Hire a driver” when a driver is available."
          : <>Not shown to customers yet: {offer.reasons.map(humanize).join(", ")}.</>}
        <div className="text-xs mt-1">{data.note}</div>
      </div>
      <fieldset disabled={!canEdit} className="grid sm:grid-cols-2 gap-3">
        <Toggle label="Driver service on" checked={form.enabled} onChange={set("enabled")} testId="ds-set-enabled" />
        <Toggle label="Offer publicly (when legally ready)" checked={form.publicly_offered} onChange={set("publicly_offered")} />
        <Toggle label="Driver must accept assignments" checked={form.acceptance_required} onChange={set("acceptance_required")} />
        <Toggle label="Driver confirms fit to drive before the trip" checked={form.fit_for_duty_required} onChange={set("fit_for_duty_required")} />
        <Toggle label="Require internal authorization" checked={form.require_internal_authorization} onChange={set("require_internal_authorization")} />
        <Toggle label="Show driver first name to customer" checked={form.show_driver_first_name} onChange={set("show_driver_first_name")} />
        <Select label="Customer price" value={form.rate_type} onChange={set("rate_type")}
                options={data.options.rate_types} />
        <label className="text-sm"><span className="text-xs text-slate-500">Rate (₱, for fixed / per day / per hour)</span>
          <input value={form.rate_pesos} onChange={set("rate_pesos")} inputMode="decimal" data-testid="ds-set-rate"
                 className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" />
          {form.rate_centavos != null && <span className="text-xs text-slate-500">Saved: {pesosFromCentavos(form.rate_centavos)}</span>}
        </label>
        <Select label="When no driver is free" value={form.availability_mode} onChange={set("availability_mode")}
                options={data.options.availability_modes} />
        <Select label="Assignment" value={form.assignment_mode} onChange={set("assignment_mode")} options={data.options.assignment_modes} />
        <Select label="Review items at dispatch" value={form.dispatch_review_policy} onChange={set("dispatch_review_policy")}
                options={data.options.dispatch_review_policies} />
        <NumberField label="Minimum notice (hours)" value={form.min_lead_time_hours} onChange={set("min_lead_time_hours")} />
        <NumberField label="Assign a driver at least (hours before)" value={form.assignment_deadline_hours} onChange={set("assignment_deadline_hours")} />
        <NumberField label="Share customer contact with driver (hours before)" value={form.contact_release_hours} onChange={set("contact_release_hours")} />
        <NumberField label="Turnaround between trips (minutes)" value={form.turnaround_buffer_minutes} onChange={set("turnaround_buffer_minutes")} />
      </fieldset>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-xs font-semibold tracking-wide text-slate-500">{form.terms_label}</div>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {Object.keys(form.service_terms || {}).map((k) => (
            <label key={k} className="text-sm"><span className="text-xs text-slate-500">{humanize(k)}</span>
              <input disabled={!canEdit} value={form.service_terms[k] || ""}
                     onChange={(e) => setForm({ ...form, service_terms: { ...form.service_terms, [k]: e.target.value } })}
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" />
            </label>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">The customer price for driver service is not what you pay the driver.
        Driver pay stays in Finance.</p>
      {canEdit && <button onClick={save} disabled={saving} data-testid="ds-settings-save"
                          className="px-4 py-2 rounded-lg btn-brand text-sm disabled:opacity-60">{saving ? "Saving…" : "Save settings"}</button>}
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 bg-white p-2">
      <input type="checkbox" checked={!!checked} onChange={onChange} data-testid={testId} /> {label}
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="text-sm"><span className="text-xs text-slate-500">{label}</span>
      <select value={value} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300">
        {options.map((o) => <option key={o} value={o}>{humanize(o)}</option>)}
      </select>
    </label>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="text-sm"><span className="text-xs text-slate-500">{label}</span>
      <input type="number" min="0" value={value ?? 0} onChange={onChange}
             className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" />
    </label>
  );
}
